/**
 * APEXCORSE 7.5 — Shopify policy HTML compatibility layer.
 * The Shopify policy renderer may output policy-embedded <style> content as
 * visible CSS text. All six original styles now live in
 * assets/apexcorse-policies-731.css; therefore only that redundant leading
 * CSS needs to be removed from the rendered DOM.
 * Legal text, links, headings and policy content are never rewritten.
 */
(() => {
  'use strict';

  if (!window.location.pathname.includes('/policies/')) return;

  const policies = [
    ['apex-contact-info', 'apex-contact-stripe'],
    ['apex-legal', 'apex-legal-stripe'],
    ['apex-privacy', 'apex-privacy-stripe'],
    ['apex-policy', 'apex-policy-stripe'],
    ['apex-shipping', 'apex-shipping-stripe'],
    ['apex-terms', 'apex-terms-stripe']
  ];

  function cssLike(text) {
    const value = (text || '').trim();
    return value.length > 120
      && /\b(?:box-sizing|grid-template-columns|font-weight|border-left|background)\s*:/.test(value)
      && /\.apex-(?:contact|legal|privacy|policy|shipping|terms)/.test(value)
      && value.includes('{') && value.includes('}');
  }

  function removeLeadingCSS(parent, stop, allowAll) {
    let removed = 0;
    let node = parent.firstChild;
    while (node && node !== stop) {
      const next = node.nextSibling;
      const isText = node.nodeType === Node.TEXT_NODE;
      const isComment = node.nodeType === Node.COMMENT_NODE;
      const isStyle = node.nodeType === Node.ELEMENT_NODE && node.tagName === 'STYLE';
      const emptyText = isText && !node.nodeValue.trim();
      const cssText = cssLike(node.textContent);
      if (allowAll || emptyText || isComment || isStyle || cssText) {
        parent.removeChild(node);
        removed++;
      }
      node = next;
    }
    return removed;
  }

  function clean() {
    const root = document.querySelector('.shopify-policy__body')
      || document.querySelector('#MainContent');
    if (!root) return;
    let removed = 0;

    for (const [outer, first] of policies) {
      const section = root.querySelector('.' + outer);
      const stripe = root.querySelector('.' + first);
      if (!stripe) continue;
      if (section && section.contains(stripe)) {
        let pivot = stripe;
        while (pivot.parentElement && pivot.parentElement !== section) {
          pivot = pivot.parentElement;
        }
        if (pivot.parentElement === section) {
          // The first designed element of each policy is the tricolour stripe.
          // Everything before it was the original CSS style element or whitespace.
          removed += removeLeadingCSS(section, pivot, true);
          section.dataset.apexPolicyClean = 'true';
          continue;
        }
      }
      // Fallback for Shopify transformations that unwrap the <section>.
      removed += removeLeadingCSS(stripe.parentNode, stripe, false);
    }

    // Fallback for policy renderers that move CSS into standalone text nodes.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const stray = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (cssLike(node.nodeValue)) stray.push(node);
    }
    for (const node of stray) {
      node.parentNode?.removeChild(node);
      removed++;
    }

    document.documentElement.dataset.apexPolicySanitized = 'true';
    document.documentElement.dataset.apexPolicyCssNodesRemoved = String(removed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', clean, { once: true });
  } else {
    clean();
  }
})();
